import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CheckCircle2, XCircle, HelpCircle, RefreshCw, TrendingUp, DollarSign, FileSpreadsheet } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AccountCreation() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [serviceInfo, setServiceInfo] = useState(null);
  const [stats, setStats] = useState(null);           // for a VA: object; for admin without va_id: array
  const [rows, setRows] = useState([]);
  const [vas, setVas] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [selectedVaId, setSelectedVaId] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [form, setForm] = useState({
    va_id: '',
    sheet_url: '',
    columns: 'C,D,E',
    username_column: 'C',
    price_total: 100,
    price_quota: 350,
    active: true,
    skip_before_row: 0,
    confirm_full_sync: false,
  });
  const [sheetPreview, setSheetPreview] = useState(null); // {last_filled_row, billable_rows, estimated_cost_if_full_sync_usd}
  const [previewing, setPreviewing] = useState(false);

  const fetchAll = async () => {
    try {
      if (isAdmin) {
        const [si, cfgs, statsRes] = await Promise.all([
          axios.get(`${API_URL}/account-creation/service-info`),
          axios.get(`${API_URL}/account-creation/configs`),
          axios.get(`${API_URL}/account-creation/stats`),
        ]);
        setServiceInfo(si.data);
        setConfigs(cfgs.data || []);
        setStats(statsRes.data);
        // Load VA list to populate the dropdown
        try {
          const vasRes = await axios.get(`${API_URL}/vas`);
          setVas((vasRes.data || []).filter(v => v.status === 'active'));
        } catch { /* optional */ }
      } else {
        const statsRes = await axios.get(`${API_URL}/account-creation/stats`);
        setStats(statsRes.data);
      }
      const rowsRes = await axios.get(`${API_URL}/account-creation/rows`, {
        params: isAdmin && selectedVaId ? { va_id: selectedVaId, limit: 100 } : { limit: 100 },
      });
      setRows(rowsRes.data || []);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load Account Creation data');
    }
  };

  useEffect(() => { fetchAll(); }, [selectedVaId]);

  const handleSaveConfig = async () => {
    if (!form.va_id || !form.sheet_url) {
      toast.error('VA and Sheet URL are required');
      return;
    }
    // 🛡️ Confirm dialog if the user tries to sync from row 0 (= check ALL history)
    const skip = parseInt(form.skip_before_row, 10) || 0;
    if (skip <= 0 && !form.confirm_full_sync) {
      const cost = sheetPreview?.estimated_cost_if_full_sync_usd ?? '?';
      const rows = sheetPreview?.billable_rows ?? '?';
      const ok = window.confirm(
        `⚠️ You are about to check ALL ${rows} existing rows in the sheet. ` +
        `This will cost approximately $${cost} in HProxy credits.\n\n` +
        `Click Cancel to keep the safe default (skip existing history and only check new rows).\n\n` +
        `Click OK ONLY if you really want to check the full history.`
      );
      if (!ok) return;
      form.confirm_full_sync = true;
    }
    try {
      const payload = {
        ...form,
        columns: form.columns.split(',').map(c => c.trim().toUpperCase()).filter(Boolean),
        price_total: parseFloat(form.price_total) || 0,
        price_quota: parseInt(form.price_quota, 10) || 1,
        skip_before_row: skip,
      };
      const res = await axios.put(`${API_URL}/account-creation/configs`, payload);
      toast.success(`Config saved. skip_before_row = ${res.data?.skip_before_row}`);
      setForm({ ...form, sheet_url: '', skip_before_row: 0, confirm_full_sync: false });
      setSheetPreview(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save config');
    }
  };

  const handlePreviewSheet = async () => {
    if (!form.sheet_url) { toast.error('Enter a sheet URL first'); return; }
    setPreviewing(true);
    try {
      const res = await axios.post(`${API_URL}/account-creation/preview-sheet`, {
        sheet_url: form.sheet_url,
        columns: form.columns.split(',').map(c => c.trim().toUpperCase()).filter(Boolean),
        username_column: form.username_column.toUpperCase(),
      });
      setSheetPreview(res.data);
      // Auto-fill the safe default
      setForm(f => ({ ...f, skip_before_row: res.data.suggested_skip_before_row }));
      toast.success(`Sheet has ${res.data.billable_rows} filled rows. Suggested skip = ${res.data.suggested_skip_before_row}.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to preview sheet');
    } finally {
      setPreviewing(false);
    }
  };

  const handleDeleteConfig = async (va_id) => {
    if (!window.confirm(`Delete the sheet config for ${va_id}?`)) return;
    try {
      await axios.delete(`${API_URL}/account-creation/configs/${va_id}`);
      toast.success('Config deleted');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleSync = async (va_id) => {
    setSyncing(true);
    try {
      const url = va_id
        ? `${API_URL}/account-creation/sync/${va_id}`
        : `${API_URL}/account-creation/sync`;
      const r = await axios.post(url);
      toast.success(`Sync done: ${JSON.stringify(r.data).slice(0, 100)}…`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  // Determine the stat object to show
  const myStats = Array.isArray(stats) ? null : stats;
  const allStats = Array.isArray(stats) ? stats : (stats ? [stats] : []);

  return (
    <div className="space-y-8" data-testid="account-creation-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold font-heading text-white tracking-tight" data-testid="page-title">
            Account Creation
          </h1>
          <p className="text-base text-muted-foreground mt-2">
            Sheet-driven Instagram account existence checker & earnings tracker
          </p>
        </div>
        {isAdmin && (
          <Button
            onClick={() => handleSync(selectedVaId || undefined)}
            disabled={syncing}
            className="bg-white text-black hover:bg-zinc-200"
            data-testid="sync-button"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing…' : 'Sync Now'}
          </Button>
        )}
      </div>

      {isAdmin && serviceInfo && (!serviceInfo.google_configured || !serviceInfo.instagram_configured) && (
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-md p-6 space-y-2" data-testid="setup-banner">
          <h3 className="text-lg font-semibold text-yellow-400">Setup required</h3>
          <ul className="text-sm text-zinc-300 list-disc ml-6 space-y-1">
            {!serviceInfo.google_configured && (
              <li>
                <strong>Google Service Account</strong> is not configured. Set
                <code className="mx-1 px-1 bg-zinc-800 rounded">GOOGLE_SERVICE_ACCOUNT_JSON</code>
                in backend env (base64-encoded JSON or raw JSON).
              </li>
            )}
            {serviceInfo.google_service_account_email && (
              <li>
                Share your Google Sheet with:
                <code className="ml-1 px-1 bg-zinc-800 rounded" data-testid="sa-email">{serviceInfo.google_service_account_email}</code>
              </li>
            )}
            {!serviceInfo.instagram_configured && (
              <li>
                <strong>HProxy API key</strong> is not configured. Set
                <code className="mx-1 px-1 bg-zinc-800 rounded">HPROXY_API_KEY</code>
                in backend env. Sign up at <a className="underline" href="https://hproxy.com/register" target="_blank" rel="noreferrer">hproxy.com</a>.
              </li>
            )}
          </ul>
        </div>
      )}

      {/* VA / user stats view */}
      {(myStats || allStats.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {(myStats ? [myStats] : allStats).map((s, i) => (
            <div key={i} className="col-span-full grid grid-cols-1 md:grid-cols-4 gap-6" data-testid={`stats-${s.va_id}`}>
              {allStats.length > 1 && (
                <div className="col-span-full text-sm text-zinc-400 uppercase tracking-wider">
                  VA: <span className="text-white">{s.va_id}</span>
                </div>
              )}
              <MetricCard title="Valid accounts" value={s.valid_count} icon={CheckCircle2} color="text-green-500" bg="bg-green-500/10" border="border-green-500/20" testid="metric-valid" />
              <MetricCard title="Not found" value={s.not_found_count} icon={XCircle} color="text-red-500" bg="bg-red-500/10" border="border-red-500/20" testid="metric-notfound" />
              <MetricCard title="Unknown (retryable)" value={s.unknown_count} icon={HelpCircle} color="text-yellow-500" bg="bg-yellow-500/10" border="border-yellow-500/20" testid="metric-unknown" />
              <MetricCard title="Earnings" value={`$${(s.earnings ?? 0).toFixed(2)}`} sub={`$${(s.price_per_valid ?? 0).toFixed(4)} / valid`} icon={DollarSign} color="text-emerald-500" bg="bg-emerald-500/10" border="border-emerald-500/20" testid="metric-earnings" />
            </div>
          ))}
        </div>
      )}

      {/* Admin: config panel */}
      {isAdmin && (
        <div className="bg-card p-6 rounded-md border border-border space-y-4" data-testid="config-panel">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-blue-500" />
            <h2 className="text-2xl font-semibold font-heading text-white">Sheet configuration</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>VA</Label>
              <Select value={form.va_id} onValueChange={(v) => setForm({ ...form, va_id: v })}>
                <SelectTrigger className="bg-zinc-900/50 border-zinc-800 text-white" data-testid="config-va-select">
                  <SelectValue placeholder="Pick a VA" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-white max-h-64">
                  {vas.map(v => (
                    <SelectItem key={v.va_id} value={v.va_id}>
                      {v.name} — {v.role || 'VA'} ({v.va_id})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Sheet URL (full URL with #gid=)</Label>
              <div className="flex gap-2">
                <Input
                  value={form.sheet_url}
                  onChange={(e) => { setForm({ ...form, sheet_url: e.target.value }); setSheetPreview(null); }}
                  placeholder="https://docs.google.com/spreadsheets/d/..."
                  className="bg-zinc-900/50 border-zinc-800 text-white"
                  data-testid="config-sheet-url"
                />
                <Button
                  type="button"
                  onClick={handlePreviewSheet}
                  disabled={previewing || !form.sheet_url}
                  variant="outline"
                  className="whitespace-nowrap"
                  data-testid="preview-sheet-btn"
                >
                  {previewing ? 'Reading…' : 'Preview'}
                </Button>
              </div>
              {sheetPreview && (
                <div className="mt-2 text-xs text-zinc-400 space-y-0.5" data-testid="sheet-preview">
                  <div>Sheet has <span className="text-white font-medium">{sheetPreview.billable_rows}</span> filled rows (last = row {sheetPreview.last_filled_row}).</div>
                  <div>Suggested <code className="text-yellow-400">skip_before_row = {sheetPreview.suggested_skip_before_row}</code> → skip all existing history.</div>
                  <div className="text-red-400">Full sync would cost ~${sheetPreview.estimated_cost_if_full_sync_usd}.</div>
                </div>
              )}
            </div>
            <div>
              <Label>Required columns (comma-separated)</Label>
              <Input
                value={form.columns}
                onChange={(e) => setForm({ ...form, columns: e.target.value })}
                className="bg-zinc-900/50 border-zinc-800 text-white"
                data-testid="config-columns"
              />
            </div>
            <div>
              <Label>Username column</Label>
              <Input
                value={form.username_column}
                onChange={(e) => setForm({ ...form, username_column: e.target.value.toUpperCase() })}
                className="bg-zinc-900/50 border-zinc-800 text-white"
                data-testid="config-username-column"
              />
            </div>
            <div>
              <Label>
                Skip before row <span className="text-yellow-500">(safety guard — check only rows ≥ this)</span>
              </Label>
              <Input
                type="number"
                value={form.skip_before_row}
                onChange={(e) => setForm({ ...form, skip_before_row: parseInt(e.target.value, 10) || 0 })}
                className="bg-zinc-900/50 border-zinc-800 text-white"
                data-testid="config-skip-before-row"
              />
              <p className="text-xs text-zinc-500 mt-1">
                0 = check ALL history (dangerous, will prompt confirmation). Click Preview to auto-fill a safe value.
              </p>
            </div>
            <div>
              <Label>Price (total $)</Label>
              <Input
                type="number"
                value={form.price_total}
                onChange={(e) => setForm({ ...form, price_total: e.target.value })}
                className="bg-zinc-900/50 border-zinc-800 text-white"
                data-testid="config-price-total"
              />
            </div>
            <div>
              <Label>Quota (nb of valid accounts)</Label>
              <Input
                type="number"
                value={form.price_quota}
                onChange={(e) => setForm({ ...form, price_quota: e.target.value })}
                className="bg-zinc-900/50 border-zinc-800 text-white"
                data-testid="config-price-quota"
              />
            </div>
          </div>
          <Button
            onClick={handleSaveConfig}
            className="bg-white text-black hover:bg-zinc-200"
            data-testid="config-save"
          >
            Save config
          </Button>

          {configs.length > 0 && (
            <div className="mt-6" data-testid="configs-list">
              <h3 className="text-lg font-semibold text-white mb-2">Existing configs</h3>
              <div className="space-y-2">
                {configs.map(c => (
                  <div key={c.va_id} className="flex items-center justify-between bg-zinc-900/50 p-3 rounded-md">
                    <div className="text-sm">
                      <div className="text-white font-medium">{c.va_id}</div>
                      <div className="text-zinc-500 text-xs">
                        cols={JSON.stringify(c.columns)} · price=${c.price_total}/{c.price_quota} · sheet_id={c.sheet_id?.slice(0, 12)}…
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => handleSync(c.va_id)} data-testid={`sync-${c.va_id}`}>
                        <RefreshCw className="w-3 h-3 mr-1" /> Sync
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => handleDeleteConfig(c.va_id)} data-testid={`delete-${c.va_id}`}>
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Recent checks */}
      <div className="bg-card p-6 rounded-md border border-border" data-testid="rows-table">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-semibold font-heading text-white flex items-center gap-2">
            <TrendingUp className="w-5 h-5" /> Recent checks
          </h2>
          {isAdmin && configs.length > 1 && (
            <Select value={selectedVaId} onValueChange={setSelectedVaId}>
              <SelectTrigger className="w-56 bg-zinc-900/50 border-zinc-800 text-white">
                <SelectValue placeholder="Filter by VA (all)" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                <SelectItem value="">All VAs</SelectItem>
                {configs.map(c => (<SelectItem key={c.va_id} value={c.va_id}>{c.va_id}</SelectItem>))}
              </SelectContent>
            </Select>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-zinc-500 uppercase text-xs">
              <tr>
                <th className="text-left p-2">Row</th>
                <th className="text-left p-2">Username</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">Checked</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={4} className="p-4 text-zinc-500 text-center">No checks yet — configure a sheet and hit Sync.</td></tr>
              )}
              {rows.map((r, i) => (
                <tr key={i} className="border-t border-zinc-800">
                  <td className="p-2 text-zinc-400">{r.row_index}</td>
                  <td className="p-2 text-white font-mono">@{r.username}</td>
                  <td className="p-2">
                    {r.status === 'found' && <span className="text-green-500 font-medium">Valid</span>}
                    {r.status === 'not_found' && <span className="text-red-500 font-medium">Not found</span>}
                    {r.status === 'unknown' && <span className="text-yellow-500 font-medium">Unknown</span>}
                  </td>
                  <td className="p-2 text-zinc-500 text-xs">{r.checked_at?.replace('T', ' ').slice(0, 19)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ title, value, sub, icon: Icon, color, bg, border, testid }) {
  return (
    <div className={`metric-card bg-card p-6 rounded-md border ${border}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">{title}</span>
        <div className={`${bg} ${color} p-2 rounded-md`}>
          <Icon className="w-5 h-5" strokeWidth={1.5} />
        </div>
      </div>
      <div className="text-4xl md:text-5xl font-bold font-heading tracking-tighter tabular-nums text-white">
        {value}
      </div>
      {sub && <div className={`mt-2 text-xs ${color}`}>{sub}</div>}
    </div>
  );
}
